/**
 * <BuyBoxPreviewPanel formData={{...}} />
 *
 * Live preview of which buy-boxes match the in-progress New Lead form,
 * BEFORE the lead is committed to the DB. Debounces to 600ms while the
 * wholesaler types — no traffic until they have at least an address + a
 * meaningful filter (city/state/price/beds).
 *
 * Endpoint: POST /api/buy-boxes/preview-match
 */
import { useEffect, useState, useRef } from "react";
import { Target, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function BuyBoxPreviewPanel({ formData }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  // Stable JSON key — only refire on field changes that actually
  // influence the match (avoids notes/seller_name churn re-firing).
  const key = JSON.stringify({
    state: (formData.state || "").trim().toUpperCase(),
    city: (formData.city || "").trim().toLowerCase(),
    zip_code: (formData.zip_code || "").trim(),
    bedrooms: formData.bedrooms,
    bathrooms: formData.bathrooms,
    sqft: formData.sqft,
    asking_price: formData.asking_price,
    estimated_arv: formData.estimated_arv,
    property_type: formData.property_type,
  });

  useEffect(() => {
    const parsed = JSON.parse(key);
    // Need at least state or city or zip — otherwise it's noise.
    const hasGeo = parsed.state || parsed.city || parsed.zip_code;
    if (!hasGeo) {
      setMatches([]);
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const payload = {
          state: parsed.state || null,
          city: parsed.city || null,
          zip_code: parsed.zip_code || null,
          bedrooms: parsed.bedrooms === "" || parsed.bedrooms == null ? null : Number(parsed.bedrooms),
          bathrooms: parsed.bathrooms === "" || parsed.bathrooms == null ? null : Number(parsed.bathrooms),
          sqft: parsed.sqft === "" || parsed.sqft == null ? null : Number(parsed.sqft),
          asking_price: parsed.asking_price === "" || parsed.asking_price == null ? null : Number(parsed.asking_price),
          estimated_arv: parsed.estimated_arv === "" || parsed.estimated_arv == null ? null : Number(parsed.estimated_arv),
          property_type: parsed.property_type || null,
        };
        const { data } = await api.post("/buy-boxes/preview-match", payload);
        setMatches(data?.matches || []);
      } catch {
        setMatches([]);
      } finally {
        setLoading(false);
      }
    }, 600);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [key]);

  if (!loading && matches.length === 0) return null;

  return (
    <div
      className="md:col-span-2 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3"
      data-testid="buy-box-preview-panel"
    >
      <div className="flex items-center gap-2 mb-2">
        <Target size={14} className="text-indigo-600 shrink-0" />
        <div className="text-xs font-extrabold text-indigo-900 uppercase tracking-wider">
          Buy-box marketplace
        </div>
        {loading && <Loader2 size={11} className="animate-spin text-indigo-500" />}
      </div>
      {loading && matches.length === 0 ? (
        <div className="text-xs text-indigo-700">Scanning buy-boxes…</div>
      ) : (
        <>
          <div className="text-sm text-indigo-900 mb-2 flex items-center gap-1.5">
            <Sparkles size={12} />
            <strong>{matches.length}</strong> active buy-box{matches.length === 1 ? "" : "es"} would match this deal.
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="buy-box-preview-chips">
            {matches.slice(0, 6).map((m) => (
              <span
                key={m.box_id}
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full bg-white text-indigo-800 border border-indigo-200"
                data-testid={`buy-box-preview-chip-${m.box_id}`}
              >
                <Target size={9} /> {m.buyer_name || "—"} · {m.box_name}
              </span>
            ))}
            {matches.length > 6 && (
              <span className="text-[11px] text-indigo-700 font-semibold px-2 py-1">
                +{matches.length - 6} more
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
